import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { GlobalRole } from '@/services/api/auth'
import { lakeApi, type UserCreateRequest, type UserInfo } from '@/services/api/lake'

export interface NewUserInput {
  username: string
  password: string
  fullName: string
  email: string
  role: GlobalRole
}

function errMessage(err: unknown, fallback: string): string {
  const e = err as { data?: { detail?: string }; message?: string }
  return e?.data?.detail || e?.message || fallback
}

/** User-administration data + actions (list, create, rename, reset password)
 *  backed by lakeApi. Toasts + refetch on each mutation; returns the affected
 *  user (or null) so the caller can re-select / close dialogs. */
export function useUsersAdmin() {
  const { t } = useTranslation()
  const [users, setUsers] = useState<UserInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      const data = await lakeApi.listUsers()
      setUsers(data)
      setSelectedUserId(prev => prev ?? (data[0]?.id || null))
    } catch {
      toast.error(t('admin.users.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    refetch()
  }, [refetch])

  const selectedUser = users.find(u => u.id === selectedUserId) || null

  const createUser = async (input: NewUserInput): Promise<UserInfo | null> => {
    try {
      const created = await lakeApi.createUser({
        username: input.username.trim(),
        password: input.password,
        full_name: input.fullName.trim() || null,
        email: input.email.trim() || null,
        role: input.role,
      } as UserCreateRequest)
      toast.success(`User "${created.username}" created`)
      await refetch()
      setSelectedUserId(created.id)
      return created
    } catch (err) {
      toast.error(errMessage(err, t('admin.users.failedToCreate')))
      return null
    }
  }

  const updateUsername = async (id: string, username: string): Promise<UserInfo | null> => {
    try {
      const updated = await lakeApi.updateUser(id, { username: username.trim() })
      toast.success(`Username updated to "${updated.username}"`)
      await refetch()
      setSelectedUserId(updated.id)
      return updated
    } catch (err) {
      toast.error(errMessage(err, t('admin.users.failedToUpdate')))
      return null
    }
  }

  const updateRole = async (id: string, role: GlobalRole): Promise<UserInfo | null> => {
    try {
      const updated = await lakeApi.updateUser(id, { role })
      toast.success(`Role updated to "${updated.role}"`)
      await refetch()
      setSelectedUserId(updated.id)
      return updated
    } catch (err) {
      toast.error(errMessage(err, t('admin.users.failedToUpdate')))
      return null
    }
  }

  const resetPassword = async (
    id: string,
    password: string,
    username: string
  ): Promise<boolean> => {
    try {
      await lakeApi.resetUserPassword(id, password)
      toast.success(`Password reset for "${username}"`)
      return true
    } catch (err) {
      toast.error(errMessage(err, t('admin.users.failedToResetPassword')))
      return false
    }
  }

  return {
    users,
    loading,
    selectedUserId,
    setSelectedUserId,
    selectedUser,
    refetch,
    createUser,
    updateUsername,
    updateRole,
    resetPassword,
  }
}
